import { Typography } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { CardStatusRow } from '../../../../queries/pxtraCard';
import { StatusCell } from './cells/status-cell';
import { CardsCell } from './cells/cards-cell';
import { PaymentRegionCell } from './cells/payment-region-cell';

const columnHelper = createColumnHelper<CardStatusRow>();

/**
 * 2.2 AK 2. The three activation reasons collapse into one chip here while
 * each row keeps the concrete one — filtering by "sign-up failed" would make
 * the manager guess which of three lists to open.
 */
const filterableStatuses = [
  'not_activated',
  'awaiting_approval',
  'issuing_failed',
  'card_pending',
  'active',
  'blocked',
  'grace_period',
  'ended'
];

export const useCardOverviewColumns = () => {
  const { t } = useTranslation();

  return [
    columnHelper.accessor((row) => `${row.first_name} ${row.last_name}`, {
      id: 'employee',
      header: t('common:NAME'),
      cell: ({ getValue }) => (
        <Typography variant="body2" className="whitespace-nowrap">
          {getValue()}
        </Typography>
      ),
      enableColumnFilter: false
    }),
    columnHelper.accessor('benefit_set_name', {
      header: t('pxtraCard:benefitSet'),
      cell: ({ getValue }) => (
        <Typography
          variant="body2"
          color={getValue() ? undefined : 'grey.500'}
          className="whitespace-nowrap"
        >
          {getValue() || '–'}
        </Typography>
      ),
      meta: { filterVariant: 'select' }
    }),
    columnHelper.accessor('status', {
      header: t('pxtraCard:cardStatus'),
      cell: ({ row }) => <StatusCell row={row.original} />,
      meta: {
        filterVariant: 'select',
        filterOptions: filterableStatuses.map((status) => ({
          value: status,
          text: t(`pxtraCard:personStatus_${status}`)
        }))
      }
    }),
    columnHelper.display({
      id: 'cards',
      header: t('pxtraCard:cards'),
      cell: ({ row }) => <CardsCell cards={row.original.cards} />,
      // Icon, digits and wallet badge on one line need room; without a floor
      // the auto layout squeezes this to the width of "Karten".
      size: 200,
      minSize: 200
    }),
    columnHelper.display({
      id: 'payment_region',
      header: t('pxtraCard:paymentRegion'),
      cell: ({ row }) => <PaymentRegionCell region={row.original.payment_region} />
    })
  ];
};
